import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import Logo from '@/components/Logo';
import {
  Users, Calendar, HandHeart, Building2, ScrollText, FileText, LogOut, Home, Crown, FileSpreadsheet, Heart, Film, Sparkles, HandMetal, BookOpen, Bell,
} from 'lucide-react';
import OverviewTab from './tabs/OverviewTab';
import MembersTab from './tabs/MembersTab';
import EventsTab from './tabs/EventsTab';
import VolunteersTab from './tabs/VolunteersTab';
import VBSVolunteersTab from './tabs/VBSVolunteersTab';
import DepartmentsTab from './tabs/DepartmentsTab';
import MessagesTab from './tabs/MessagesTab';
import PrayersTab from './tabs/PrayersTab';
import AuditTab from './tabs/AuditTab';
import RegistrationsTab from './tabs/RegistrationsTab';
import DonationsTab from './tabs/DonationsTab';
import MediaTab from './tabs/MediaTab';
import LeafletsTab from './tabs/LeafletsTab';
import PushBroadcastTab from './tabs/PushBroadcastTab';

const TAB_DEFS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'registrations', label: 'Registrations', icon: FileSpreadsheet },
  { id: 'vbs-volunteers', label: 'VBS Volunteers', icon: Sparkles },
  { id: 'donations', label: 'Donations', icon: Heart },
  { id: 'media', label: 'Media', icon: Film },
  { id: 'leaflets', label: 'Leaflets', icon: BookOpen },
  { id: 'push', label: 'Push Notifications', icon: Bell },
  { id: 'volunteers', label: 'Volunteers', icon: HandHeart },
  { id: 'departments', label: 'Departments', icon: Building2 },
  { id: 'prayers', label: 'Prayer Requests', icon: HandMetal },
  { id: 'messages', label: 'Messages', icon: FileText },
  { id: 'audit', label: 'Audit Logs', icon: ScrollText },
];

function TabBar({ tabs, current, onChange }) {
  // The scroll-fade overlays hint on mobile that more tabs exist to the right;
  // pointer-events-none keeps them non-interactive so taps always hit the real buttons.
  return (
    <div className="relative" data-testid="admin-tab-bar">
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-selected={current === t.id}
            onClick={() => onChange(t.id)}
            className={`flex items-center gap-2 px-4 min-h-[44px] rounded-full text-sm font-semibold whitespace-nowrap transition touch-manipulation ${current === t.id ? 'bg-moj-purple text-white' : 'bg-white text-moj-purple-deep border border-moj-gold/30 hover:bg-moj-cream-soft active:bg-moj-cream'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={16}/> {t.label}
          </button>
        ))}
      </div>
      <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-moj-cream to-transparent sm:hidden"/>
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-moj-cream to-transparent sm:hidden"/>
    </div>
  );
}

function PortalHeader({ isPastor, onLogout }) {
  // Mobile-first layout: logo + role title on one tight row, action buttons on
  // the next so neither can get squeezed into an ellipsis. On sm+ screens the
  // whole thing collapses back onto a single row with buttons on the right.
  return (
    <header className="gradient-purple text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile: icon-only logo so the role title has breathing room.
              Desktop: full wordmark for brand reinforcement. */}
          <div className="sm:hidden"><Logo size="sm" showText={false} light/></div>
          <div className="hidden sm:block"><Logo size="md" light/></div>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-lg sm:text-xl leading-tight truncate">{isPastor ? 'Pastor Portal' : 'Admin Portal'}</h1>
            {isPastor && (
              <div className="text-[11px] sm:text-xs text-moj-gold flex items-center gap-1">
                <Crown size={12}/> Super User Access
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 self-stretch sm:self-auto">
          <Link
            to="/"
            data-testid="admin-view-site"
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 min-h-[40px] px-3 rounded-full text-sm font-semibold text-white border border-white/30 hover:bg-white/10 active:bg-white/15 transition"
          >
            <Home size={14}/> View site
          </Link>
          <button
            onClick={onLogout}
            className="flex-1 sm:flex-none btn-gold text-sm !py-2 !px-3 justify-center"
            data-testid="admin-logout"
          >
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function readStoredProfile() {
  try {
    return JSON.parse(localStorage.getItem('moj_admin_profile') || 'null');
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Could not parse stored admin profile:', err);
    }
    localStorage.removeItem('moj_admin_profile');
    return null;
  }
}

function useAdminSession(pastorMode) {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('moj_admin_token');
    if (!token) {
      nav(pastorMode ? '/pastor/login' : '/admin/login');
      return;
    }
    setProfile(readStoredProfile());
  }, [nav, pastorMode]);

  const logout = () => {
    localStorage.removeItem('moj_admin_token');
    localStorage.removeItem('moj_admin_profile');
    nav(pastorMode ? '/pastor/login' : '/admin/login');
  };

  return { profile, logout };
}

function TabContent({ current, isPastor, stats, profile }) {
  switch (current) {
    case 'overview':      return <OverviewTab stats={stats} profile={profile}/>;
    case 'members':       return <MembersTab isPastor={isPastor}/>;
    case 'events':        return <EventsTab/>;
    case 'registrations': return <RegistrationsTab/>;
    case 'vbs-volunteers': return <VBSVolunteersTab/>;
    case 'donations':     return <DonationsTab/>;
    case 'media':         return <MediaTab/>;
    case 'leaflets':      return <LeafletsTab/>;
    case 'push':          return <PushBroadcastTab/>;
    case 'volunteers':    return <VolunteersTab/>;
    case 'departments':   return <DepartmentsTab/>;
    case 'prayers':       return <PrayersTab/>;
    case 'messages':      return <MessagesTab/>;
    case 'audit':         return <AuditTab/>;
    default: return null;
  }
}

export default function AdminPortal({ pastorMode = false }) {
  const [tab, setTab] = useState('overview');
  const { profile, logout } = useAdminSession(pastorMode);
  const { data: stats } = useQuery({
    queryKey: ['adm-stats', pastorMode],
    queryFn: async () => (await api.get(pastorMode ? '/pastor/dashboard-summary' : '/admin/stats')).data,
  });
  const isPastor = pastorMode || profile?.role === 'pastor';

  return (
    <div className="min-h-screen bg-moj-cream">
      <PortalHeader isPastor={isPastor} onLogout={logout}/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <TabBar tabs={TAB_DEFS} current={tab} onChange={setTab}/>
        <div className="mt-6">
          <TabContent current={tab} isPastor={isPastor} stats={stats} profile={profile}/>
        </div>
      </div>
    </div>
  );
}
